import { normalizeAssetUrl } from '../../../utils/urls';
import { fetchMseManifestJson } from './net';

/**
 * `clip.mp4` lives next to `clip.mse/manifest.json`.
 *
 * Folder layout (chosen):
 *   clip.mp4
 *   clip.mse/
 *     manifest.json
 *     init.mp4
 *     seg-00001.m4s
 *     seg-00002.m4s
 *     ...
 *
 * We require ALL clips to be packed with the same codecs/settings so that MSE can append them sequentially.
 */
export type MseClipManifest = {
  version: 1;
  /**
   * MSE SourceBuffer mime, must be supported by `MediaSource.isTypeSupported(mime)`.
   * Example: `video/mp4; codecs="avc1.42E01E, mp4a.40.2"`
   */
  mime: string;
  /** Relative path to init segment (typically `init.mp4`). */
  init: string;
  /** Relative paths to media segments (typically `seg-00001.m4s`, ...). */
  segments: string[];
  /** Total clip duration in milliseconds (used by controller for boundaries). */
  totalDurationMs: number;
  /** Segment duration in milliseconds (informational; helps debugging). */
  segmentDurationMs: number;
};

export function mseManifestUrlForMp4(mp4Url: string): string {
  const u = normalizeAssetUrl(mp4Url);
  // If we can't parse, keep best-effort.
  if (!u) return '';
  // Replace trailing .mp4 with .mse/manifest.json
  if (u.toLowerCase().endsWith('.mp4')) {
    return `${u.slice(0, -4)}.mse/manifest.json`;
  }
  // If caller passes something else (already folder), attempt to append.
  return u.endsWith('/') ? `${u}manifest.json` : `${u}/manifest.json`;
}

export async function loadMseManifest(
  manifestUrl: string,
  opts?: { onRecovered?: (meta: any) => void },
): Promise<{ manifest: MseClipManifest; baseUrl: string }> {
  const u = normalizeAssetUrl(manifestUrl);
  if (!u) throw new Error('Bad manifest url');
  let json: any = null;
  const r = await fetchMseManifestJson(u, {
    retry: { attempts: 3, baseDelayMs: 250, backoffFactor: 2, maxDelayMs: 4000, jitterRatio: 0.2 },
    stall: { stallTimeoutMs: 3500, attemptTimeoutMs: 20000 },
    onRecovered: opts?.onRecovered,
  });
  json = r.json;

  if (!json || json.version !== 1) throw new Error(`Invalid manifest: ${u}`);
  if (!json.mime || !json.init || !Array.isArray(json.segments)) throw new Error(`Invalid manifest fields: ${u}`);
  if (!json.totalDurationMs || !json.segmentDurationMs) throw new Error(`Missing duration fields: ${u}`);
  const baseUrl = u.slice(0, u.lastIndexOf('/') + 1);
  return { manifest: json as MseClipManifest, baseUrl };
}

export function resolveManifestRelative(baseUrl: string, rel: string): string {
  const r = String(rel || '').trim();
  if (!r) return '';
  // absolute URLs are kept
  if (/^https?:\/\//i.test(r)) return r;
  if (r.startsWith('/')) return r;
  return `${baseUrl}${r}`;
}


